import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useMutation } from "@tanstack/react-query";
import logger from '@/lib/logger';
import { api } from "@/lib/api";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { LoadingState } from "@/components/loading-state";
import type { UserFormData } from "@/types";
import { trackEvent } from "@/lib/analytics/posthog";
import { SEO } from "@/components/seo";

const formSchema = z.object({
  // Personal Information
  height: z.preprocess(
    (val) => val === "" || val === undefined || val === null ? undefined : Number(val),
    z.number().min(120, "Height is required (minimum 120cm)").max(250, "Height must be under 250cm")
  ),
  weight: z.preprocess(
    (val) => val === "" || val === undefined || val === null ? undefined : Number(val),
    z.number().min(30, "Weight is required (minimum 30kg)").max(200, "Weight must be under 200kg")
  ),
  age: z.preprocess(
    (val) => val === "" || val === undefined || val === null ? undefined : Number(val),
    z.number().min(12, "Age is required (minimum 12 years)").max(80, "Age must be under 80")
  ),
  gender: z.string().min(1, "Gender is required"),
  
  // Jumping Style
  jumpingStyle: z.string().min(1, "Jumping style is required"),
  
  // Training Commitment
  trainingFrequency: z.string().min(1, "Training frequency is required"),
  
  // Free Weights Equipment
  dumbbells: z.array(z.string()).default([]),
  kettlebells: z.array(z.string()).default([]),
  weightPlates: z.array(z.string()).default([]),
  barbells: z.array(z.string()).default([]),
  legPressAccess: z.array(z.string()).default([]),
  
  // Weighted Balls & Bags
  medicineBalls: z.array(z.string()).default([]),
  sandbags: z.array(z.string()).default([]),
  
  // Explosiveness & Agility Tools
  jumpSurfaces: z.array(z.string()).default([]),
  jumpRope: z.array(z.string()).default([]),
  resistanceBands: z.boolean().default(false),
  // Make agility ladder optional for steps beyond the first page
  agilityLadder: z.string().optional().or(z.literal("")),
  
  // Loaded Carries & Sleds
  farmersCarry: z.array(z.string()).default([]),
  sledAccess: z.array(z.string()).default([]),
  
  // Mobility & Recovery
  mobilityEquipment: z.array(z.string()).default([]),
  
  // Racks
  // Make rack access optional
  rackAccess: z.string().optional().or(z.literal("")),
  
  // Court Access
  // Make court access optional
  courtAccess: z.string().optional().or(z.literal("")),
  
  // Injury History
  injuryHistory: z.array(z.string()).default([]),
  otherInjury: z.string().optional(),
  
  // Optional Stats
  standingVertical: z.coerce.number().optional().or(z.literal("")),
  runningVertical: z.coerce.number().optional().or(z.literal("")),
  armSpan: z.coerce.number().optional().or(z.literal("")),
  squatMax: z.coerce.number().optional().or(z.literal("")),
  deadliftMax: z.coerce.number().optional().or(z.literal("")),
});

export default function FormPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { isAuthenticated, isLoading, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5;

  // Development-only bypass to allow viewing the form without auth when explicitly enabled
  const devBypass = (import.meta as any).env?.DEV && typeof window !== 'undefined' && (
    (() => {
      try {
        const qs = new URLSearchParams(window.location.search);
        return (
          qs.get('dev') === '1' ||
          (window as any).DEV_ALLOW_FORM === '1' ||
          localStorage.getItem('DEV_ALLOW_FORM') === '1'
        );
      } catch {
        return false;
      }
    })()
  );
  const isAuthedEffective = isAuthenticated || devBypass;

  useEffect(() => {
    // Redirect to login if not authenticated (unless dev bypass is enabled)
    if (!isLoading && !isAuthedEffective) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, isLoading, navigate, devBypass]);

  const form = useForm<UserFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      // Personal Information
      height: "" as any,
      weight: "" as any,
      age: "" as any,
      gender: "",
      
      // Jumping Style
      jumpingStyle: "",
      
      // Training Commitment
      trainingFrequency: "",
      
      // Free Weights Equipment
      dumbbells: [],
      kettlebells: [],
      weightPlates: [],
      barbells: [],
      legPressAccess: [],
      
      // Weighted Balls & Bags
      medicineBalls: [],
      sandbags: [],
      
      // Explosiveness & Agility Tools
      jumpSurfaces: [],
      jumpRope: [],
      resistanceBands: false,
      agilityLadder: "",
      
      // Loaded Carries & Sleds
      farmersCarry: [],
      sledAccess: [],
      
      // Mobility & Recovery
      mobilityEquipment: [],
      
      // Racks
      rackAccess: "",
      
      // Court Access
      courtAccess: "",
      
      // Injury History
      injuryHistory: [],
      otherInjury: "",
      
      // Optional Stats
      standingVertical: undefined,
      runningVertical: undefined,
      armSpan: undefined,
      squatMax: undefined,
      deadliftMax: undefined,
    },
  });

  // State to store previous selections when "None" is unchecked
  const previousSelections = useRef<{ [key: string]: string[] }>({});

  // Helper function for cascading weight range selection
  const handleWeightRangeToggle = (
    fieldName: string,
    weightOptions: string[],
    checked: boolean,
    checkedWeight: string,
    currentValue: string[],
    onChange: (value: string[]) => void
  ) => {
    if (checkedWeight === "None") {
      if (checked) {
        // Store previous non-None selections then select only "None"
        previousSelections.current[fieldName] = currentValue.filter((w) => w !== "None");
        onChange(["None"]);
      } else {
        // Restore previous selections when None is unchecked
        const previousNonNone = previousSelections.current[fieldName] || [];
        onChange(previousNonNone);
      }
      return;
    }

    if (checked) {
      // Remove "None" and cascade all lower/equal ranges
      const withoutNone = currentValue.filter((w) => w !== "None");
      const idx = weightOptions.indexOf(checkedWeight);
      const cascaded = weightOptions.slice(0, idx + 1).filter((w) => w !== "None");
      const merged = withoutNone.concat(cascaded.filter((w) => !withoutNone.includes(w)));
      onChange(merged);
    } else {
      // Allow holes when unchecking (Option B)
      onChange(currentValue.filter((w) => w !== checkedWeight));
    }
  };

  const generatePlanMutation = useMutation({
    mutationFn: api.generatePlan,
    onSuccess: (data) => {
      logger.debug("Plan generation successful - Full response:", data);
      logger.debug("Response structure - has planData:", !!data?.planData);
      logger.debug("planData content:", data?.planData);
      try {
        const formValues = form.getValues();
        const daysCount = Array.isArray(data?.planData?.days) ? data.planData.days.length : 0;
        trackEvent('plan_generated', {
          days_count: daysCount,
          training_frequency: formValues.trainingFrequency,
          has_injury_history: Array.isArray(formValues.injuryHistory) && formValues.injuryHistory.length > 0,
        });
      } catch {}
      
      if (!data || !data.planData) {
        logger.error("ERROR: Response missing planData!", data);
        toast({
          title: "Error",
          description: "Plan generation succeeded but response format is invalid",
          variant: "destructive",
        });
        return;
      }
      
      logger.info("Fresh plan received from Tencent agent - storing in localStorage");
      // Store the freshly generated plan in localStorage for the plan page
      localStorage.setItem("generatedPlan", JSON.stringify(data.planData));
      localStorage.setItem("isNewGeneration", "true"); // Flag to indicate this is fresh data
      logger.debug("Stored in localStorage - generatedPlan:", localStorage.getItem("generatedPlan")?.substring(0, 100));
      logger.debug("Stored in localStorage - isNewGeneration:", localStorage.getItem("isNewGeneration"))
      
      // Save form data to localStorage for "View My Stats" feature
      const formData = form.getValues();
      const processedFormData = {
        ...formData,
        standingVertical: formData.standingVertical || undefined,
        runningVertical: formData.runningVertical || undefined,
        armSpan: formData.armSpan || undefined,
        squatMax: formData.squatMax || undefined,
        deadliftMax: formData.deadliftMax || undefined,
        otherInjury: formData.otherInjury === "" ? undefined : formData.otherInjury,
      };
      localStorage.setItem("formData", JSON.stringify(processedFormData));
      logger.debug("Form data saved to localStorage:", processedFormData);
      
      navigate("/plan");
    },
    onError: (error) => {
      logger.error("Plan generation error:", error as any);
      trackEvent('plan_generation_failed', {
        error_message: error.message || 'unknown',
      });
      toast({
        title: "Error",
        description: error.message || "Failed to generate plan. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Track form abandonment when user navigates away mid-form
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentStep > 0 && !generatePlanMutation.isPending && !generatePlanMutation.isSuccess) {
        trackEvent('form_abandoned', { current_step: currentStep });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentStep, generatePlanMutation.isPending, generatePlanMutation.isSuccess]);

  const onSubmit = (data: UserFormData) => {
    // Check if user has sufficient credits
    if (!user || user.credits <= 0) {
      toast({
        title: "Insufficient Credits",
        description: "You need at least 1 credit to generate a training plan. Please purchase credits or refer friends to earn more.",
        variant: "destructive",
      });
      try {
        trackEvent('plan_generation_blocked', { reason: 'insufficient_credits' });
      } catch {}
      return;
    }

    logger.debug("Form submission data:", data);
    logger.debug("Form errors:", form.formState.errors);
    logger.debug("Form is valid:", form.formState.isValid);
    
    // Clear any existing cached plan data to ensure fresh generation
    localStorage.removeItem("generatedPlan");
    logger.info("Cleared cached plan data - generating fresh plan from Tencent agent");
    
    // Clean up the data for API submission
    const processedData = {
      ...data,
      userId: user?.id?.toString(),
      standingVertical: data.standingVertical || undefined,
      runningVertical: data.runningVertical || undefined,
      armSpan: data.armSpan || undefined,
      squatMax: data.squatMax || undefined,
      deadliftMax: data.deadliftMax || undefined,
      // New PR-related fields
      prUnit: data.prUnit || undefined,
      prType: data.prType || undefined,
      bulgarianSplitSquatPR: data.bulgarianSplitSquatPR || undefined,
      otherInjury: data.otherInjury === "" ? undefined : data.otherInjury,
    };
    
    logger.debug("Processed data for API:", processedData);
    logger.info("Starting mutation - calling Tencent agent (this will take 5+ minutes)...");
    try {
      trackEvent('plan_generation_started', {
        training_frequency: data.trainingFrequency,
        has_injury_history: Array.isArray(data.injuryHistory) && data.injuryHistory.length > 0,
      });
    } catch {}
    generatePlanMutation.mutate(processedData);
  };

  const validateCurrentStep = async () => {
    const stepFields: Record<number, string[]> = {
      1: ['height', 'weight', 'age', 'gender', 'jumpingStyle', 'trainingFrequency'],
      2: [], // All fields on this and subsequent steps are optional
      3: [], // All fields on this and subsequent steps are optional  
      4: [], // All fields on this and subsequent steps are optional
      5: [] // All fields on this and subsequent steps are optional
    };

    const fieldsToValidate = stepFields[currentStep] || [];
    if (fieldsToValidate.length === 0) return true;

    // Get current form values to debug
    const currentValues = form.getValues();
    logger.debug(`Step ${currentStep} current values:`, currentValues);
    logger.debug(`Fields to validate:`, fieldsToValidate);

    // For step 1, we need to check if the values are actually filled in
    if (currentStep === 1) {
      const requiredFields = {
        height: currentValues.height,
        weight: currentValues.weight,
        age: currentValues.age,
        gender: currentValues.gender,
        jumpingStyle: currentValues.jumpingStyle,
        trainingFrequency: currentValues.trainingFrequency,
      };

      // Check if all required fields have valid values
      const isValid = Object.entries(requiredFields).every(([field, value]) => {
        if (field === 'height') {
          const numValue = Number(value);
          const isValidNumber = !isNaN(numValue) && numValue >= 120 && numValue <= 250 && value !== "" && value !== null && value !== undefined;
          logger.debug(`Field ${field}: value="${value}", numValue=${numValue}, isValid=${isValidNumber}`);
          return isValidNumber;
        } else if (field === 'weight') {
          const numValue = Number(value);
          const isValidNumber = !isNaN(numValue) && numValue >= 30 && numValue <= 200 && value !== "" && value !== null && value !== undefined;
          logger.debug(`Field ${field}: value="${value}", numValue=${numValue}, isValid=${isValidNumber}`);
          return isValidNumber;
        } else if (field === 'age') {
          const numValue = Number(value);
          const isValidNumber = !isNaN(numValue) && numValue >= 12 && numValue <= 80 && value !== "" && value !== null && value !== undefined;
          logger.debug(`Field ${field}: value="${value}", numValue=${numValue}, isValid=${isValidNumber}`);
          return isValidNumber;
        } else {
          // For string fields, just check they're not empty
          const isValidString = value && typeof value === 'string' && value.trim() !== "";
          logger.debug(`Field ${field}: value="${value}", isValid=${isValidString}`);
          return isValidString;
        }
      });

      logger.debug(`Step 1 manual validation result:`, isValid);
      return isValid;
    }

    // For other steps, use the form's trigger validation
    const result = await form.trigger(fieldsToValidate as any);
    logger.debug(`Step ${currentStep} validation result:`, result);
    logger.debug(`Step ${currentStep} errors:`, form.formState.errors);
    
    return result;
  };

  const nextStep = async () => {
    logger.debug("nextStep called. Current step:", currentStep, "Total steps:", totalSteps);
    
    const isStepValid = await validateCurrentStep();
    if (!isStepValid) {
      if (devBypass) {
        logger.warn("Dev bypass enabled: skipping validation for step", currentStep);
      } else {
        toast({
          title: "Form Incomplete",
          description: "Please fill out all required fields before proceeding to the next step.",
          variant: "destructive",
        });
        return;
      }
    }

    if (currentStep < totalSteps) {
      const next = currentStep + 1;
      setCurrentStep(next);
      logger.debug("Moving to step:", next);
      try {
        trackEvent('form_step_viewed', { step: next });
      } catch {}
    } else {
      logger.debug("Already at last step, cannot go further");
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepTitle = (step: number) => {
    switch (step) {
      case 1: return "Personal Information & Training Commitment";
      case 2: return "Free Weights Equipment";
      case 3: return "Specialized Equipment";
      case 4: return "Equipment & Facilities";
      case 5: return "Injury History & Optional Stats";
      default: return "";
    }
  };

  const getStepColor = (step: number) => {
    switch (step) {
      case 1: return "text-white";
      case 2: return "text-white";
      case 3: return "text-white";
      case 4: return "text-white";
      case 5: return "text-white";
      default: return "text-white";
    }
  };

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:bg-gradient-to-br dark:from-[var(--dark-1)] dark:to-[var(--dark-2)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render anything if not authenticated (redirect is handled in useEffect)
  if (!isAuthedEffective) {
    return null;
  }

  if (generatePlanMutation.isPending) {
    return <LoadingState />;
  }

  const OptionalBanner = () => (
    <div className="mb-4 rounded-2xl border-2 border-gray-200 dark:border-[var(--dark-3)] bg-white/5 dark:bg-white/5 p-4">
      <p className="text-sm text-white">These questions are optional.</p>
    </div>
  );

  return (
    <section className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:bg-gradient-to-br dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
      <SEO
        title="Create Your Training Plan | BounceBunny"
        description="Tell us about yourself and get a personalized AI-generated basketball training plan. Tailored to your height, weight, goals, and available equipment."
        canonical="https://bouncebunny.app/form"
      />
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Tell Us About Yourself</h2>
            <p className="text-white text-lg">
              We'll use this information to create your personalized training plan
            </p>
          </div>

          <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-2xl">
            <CardContent className="p-8">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                  {/* Progress Bar */}
                  <div className="mb-8">
                    <div className="flex justify-between text-sm font-medium mb-2">
                      <span className="text-white">Step {currentStep} of {totalSteps}</span>
                      <span className="text-white">{Math.round((currentStep / totalSteps) * 100)}% Complete</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-[var(--dark-3)] rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-gray-700 to-gray-400 dark:from-gray-300 dark:to-gray-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Step 1: Personal Information */}
                  {currentStep === 1 && (
                    <div className="form-section">
                      <h3 className={`text-2xl font-semibold mb-6 ${getStepColor(1)}`}>
                        {getStepTitle(1)}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FormField
                          control={form.control}
                          name="height"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Height (cm)</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  placeholder="Enter height in cm"
                                  className="input-focus rounded-2xl"
                                  {...field}
                                  onChange={(e) => field.onChange(Number(e.target.value))}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="weight"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Weight (kg)</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  placeholder="Enter weight in kg"
                                  className="input-focus rounded-2xl"
                                  {...field}
                                  onChange={(e) => field.onChange(Number(e.target.value))}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="age"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Age</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  placeholder="Enter age"
                                  className="input-focus rounded-2xl"
                                  {...field}
                                  onChange={(e) => field.onChange(Number(e.target.value))}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="gender"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Gender</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger className="input-focus rounded-2xl">
                                    <SelectValue placeholder="Select gender" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="male">Male</SelectItem>
                                  <SelectItem value="female">Female</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="jumpingStyle"
                          render={({ field }) => (
                            <FormItem className="md:col-span-2">
                              <FormLabel>How do you usually jump when trying to dunk or jump high?</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger className="input-focus rounded-2xl">
                                    <SelectValue placeholder="Select jumping style" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="one_foot">One foot</SelectItem>
                                  <SelectItem value="two_feet">Two feet</SelectItem>
                                  <SelectItem value="both_equally">Both equally</SelectItem>
                                  <SelectItem value="not_sure">Not sure</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      {/* Training Frequency */}
                      <div className="mt-8">
                        <FormField
                          control={form.control}
                          name="trainingFrequency"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-lg font-medium">How many days per week can you consistently train?</FormLabel>
                              <div className="grid grid-cols-1 xs:grid-cols-3 gap-3 sm:gap-4 mt-4">
                                {[
                                  { value: "4", label: "4 days/week" },
                                  { value: "5", label: "5 days/week" },
                                  { value: "6", label: "6 days/week" }
                                ].map((option) => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => field.onChange(option.value)}
                                    className={`p-3 sm:p-4 rounded-2xl border-2 transition-all text-center ${
                                      field.value === option.value
                                        ? "border-[var(--apple-green)] bg-green-50 dark:bg-green-900/20"
                                        : "border-gray-200 dark:border-[var(--dark-3)] hover:border-gray-400"
                                    }`}
                                  >
                                    <div className="font-medium">{option.label}</div>
                                  </button>
                                ))}
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 2: Free Weights Equipment */}
                  {currentStep === 2 && (
                    <div className="form-section">
                      <h3 className={`text-2xl font-semibold mb-6 ${getStepColor(2)}`}>
                        Free Weights Equipment
                      </h3>
                      <OptionalBanner />
                      <div className="space-y-8">
                        {/* Free Weights Equipment */}
                        <div className="space-y-6">
                          <h4 className="text-lg font-medium text-white">Free Weights (Select all weight ranges you have access to)</h4>
                          
                          {/* Dumbbells */}
                          <FormField
                            control={form.control}
                            name="dumbbells"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Dumbbells</FormLabel>
                                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                                  {(["1–10 kg", "11–20 kg", "21–40 kg", "41+ kg", "None"]).map((weight) => (
                                    <label key={weight} className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={field.value?.includes(weight)}
                                        onCheckedChange={(checked) => {
                                          handleWeightRangeToggle(
                                            "dumbbells",
                                            ["1–10 kg", "11–20 kg", "21–40 kg", "41+ kg", "None"],
                                            checked === true,
                                            weight,
                                            field.value || [],
                                            field.onChange
                                          );
                                        }}
                                      />
                                      <span className="text-sm text-white">{weight}</span>
                                    </label>
                                  ))}
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Kettlebells */}
                          <FormField
                            control={form.control}
                            name="kettlebells"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Kettlebells</FormLabel>
                                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                                  {(["1–8 kg", "9–16 kg", "17–24 kg", "25+ kg", "None"]).map((weight) => (
                                    <label key={weight} className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={field.value?.includes(weight)}
                                        onCheckedChange={(checked) => {
                                          handleWeightRangeToggle(
                                            "kettlebells",
                                            ["1–8 kg", "9–16 kg", "17–24 kg", "25+ kg", "None"],
                                            checked === true,
                                            weight,
                                            field.value || [],
                                            field.onChange
                                          );
                                        }}
                                      />
                                      <span className="text-sm text-white">{weight}</span>
                                    </label>
                                  ))}
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Weight Plates */}
                          <FormField
                            control={form.control}
                            name="weightPlates"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Weight Plates (In Total)</FormLabel>
                                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                                  {(["Up to 10 kg", "11–60 kg", "61–100 kg", "Over 100 kg", "None"]).map((weight) => (
                                    <label key={weight} className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={field.value?.includes(weight)}
                                        onCheckedChange={(checked) => {
                                          handleWeightRangeToggle(
                                            "weightPlates",
                                            ["Up to 10 kg", "11–60 kg", "61–100 kg", "Over 100 kg", "None"],
                                            checked === true,
                                            weight,
                                            field.value || [],
                                            field.onChange
                                          );
                                        }}
                                      />
                                      <span className="text-sm text-white">{weight}</span>
                                    </label>
                                  ))}
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Barbells */}
                          <FormField
                            control={form.control}
                            name="barbells"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Barbells</FormLabel>
                                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                                  {(["1–20 kg", "21–40 kg", "41–60 kg", "61+ kg", "None"]).map((weight) => (
                                    <label key={weight} className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={field.value?.includes(weight)}
                                        onCheckedChange={(checked) => {
                                          handleWeightRangeToggle(
                                            "barbells",
                                            ["1–20 kg", "21–40 kg", "41–60 kg", "61+ kg", "None"],
                                            checked === true,
                                            weight,
                                            field.value || [],
                                            field.onChange
                                          );
                                        }}
                                      />
                                      <span className="text-sm text-white">{weight}</span>
                                    </label>
                                  ))}
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Leg Press Machine */}
                          <FormField
                            control={form.control}
                            name="legPressAccess"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Leg Press Machine</FormLabel>
                                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                                  {(["Up to 100 kg", "101–150 kg", "151–200 kg", "201+ kg", "None"]).map((weight) => (
                                    <label key={weight} className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={field.value?.includes(weight)}
                                        onCheckedChange={(checked) => {
                                          handleWeightRangeToggle(
                                            "legPressAccess",
                                            ["Up to 100 kg", "101–150 kg", "151–200 kg", "201+ kg", "None"],
                                            checked === true,
                                            weight,
                                            field.value || [],
                                            field.onChange
                                          );
                                        }}
                                      />
                                      <span className="text-sm text-white">{weight}</span>
                                    </label>
                                  ))}
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Specialized Equipment */}
                  {currentStep === 3 && (
                    <div className="form-section">
                      <h3 className={`text-2xl font-semibold mb-6 ${getStepColor(3)}`}>
                        {getStepTitle(3)}
                      </h3>
                      <OptionalBanner />
                      <div className="space-y-8">
                        {/* Weighted Balls & Bags */}
                        <div className="space-y-6">
                          <h4 className="text-lg font-medium text-white">Weighted Balls & Bags (Select all weight ranges)</h4>
                          
                          {/* Medicine Balls */}
                          <FormField
                            control={form.control}
                            name="medicineBalls"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Medicine Balls</FormLabel>
                                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                                  {(["1–4 kg", "5–8 kg", "9–12 kg", "13+ kg", "None"]).map((weight) => (
                                    <label key={weight} className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={field.value?.includes(weight)}
                                        onCheckedChange={(checked) => {
                                          handleWeightRangeToggle(
                                            "medicineBalls",
                                            ["1–4 kg", "5–8 kg", "9–12 kg", "13+ kg", "None"],
                                            checked === true,
                                            weight,
                                            field.value || [],
                                            field.onChange
                                          );
                                        }}
                                      />
                                      <span className="text-sm text-white">{weight}</span>
                                    </label>
                                  ))}
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Sandbags / Bulgarian Bags */}
                          <FormField
                            control={form.control}
                            name="sandbags"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Sandbags / Bulgarian Bags</FormLabel>
                                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                                  {(["Up to 10 kg", "11–20 kg", "21–30 kg", "31+ kg", "None"]).map((weight) => (
                                    <label key={weight} className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={field.value?.includes(weight)}
                                        onCheckedChange={(checked) => {
                                          handleWeightRangeToggle(
                                            "sandbags",
                                            ["Up to 10 kg", "11–20 kg", "21–30 kg", "31+ kg", "None"],
                                            checked === true,
                                            weight,
                                            field.value || [],
                                            field.onChange
                                          );
                                        }}
                                      />
                                      <span className="text-sm text-white">{weight}</span>
                                    </label>
                                  ))}
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        {/* Explosiveness & Agility Tools */}
                        <div className="space-y-6">
                          <h4 className="text-lg font-medium text-white">Explosiveness & Agility Tools (Select all that apply)</h4>
                          
                          {/* Jump Surfaces */}
                          <FormField
                            control={form.control}
                            name="jumpSurfaces"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Jump Surfaces</FormLabel>
                                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-4 gap-3">
                                  {["Plyometric box", "Sturdy chair", "Stairs", "None"].map((surface) => (
                                    <label key={surface} className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={field.value?.includes(surface)}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            field.onChange([...field.value, surface]);
                                          } else {
                                            field.onChange(field.value?.filter(s => s !== surface));
                                          }
                                        }}
                                      />
                                      <span className="text-sm text-white">{surface}</span>
                                    </label>
                                  ))}
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Jump Rope */}
                          <FormField
                            control={form.control}
                            name="jumpRope"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Jump Rope</FormLabel>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  {["Speed rope", "Heavy rope", "None"].map((rope) => (
                                    <label key={rope} className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={field.value?.includes(rope)}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            field.onChange([...field.value, rope]);
                                          } else {
                                            field.onChange(field.value?.filter(r => r !== rope));
                                          }
                                        }}
                                      />
                                      <span className="text-sm text-white">{rope}</span>
                                    </label>
                                  ))}
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Resistance Bands */}
                          <FormField
                            control={form.control}
                            name="resistanceBands"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Resistance Bands</FormLabel>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <button
                                    type="button"
                                    onClick={() => field.onChange(true)}
                                    className={`p-4 rounded-2xl border-2 transition-all text-center ${
                                      field.value === true
                                        ? "border-[var(--apple-green)] bg-green-50 dark:bg-green-900/20"
                                        : "border-gray-200 dark:border-[var(--dark-3)] hover:border-gray-400 dark:hover:border-gray-500"
                                    }`}
                                  >
                                    <div className="font-medium">Yes</div>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => field.onChange(false)}
                                    className={`p-4 rounded-2xl border-2 transition-all text-center ${
                                      field.value === false
                                        ? "border-[var(--apple-green)] bg-green-50 dark:bg-green-900/20"
                                        : "border-gray-200 dark:border-[var(--dark-3)] hover:border-gray-400 dark:hover:border-gray-500"
                                    }`}
                                  >
                                    <div className="font-medium">No</div>
                                  </button>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Agility Ladder */}
                          <FormField
                            control={form.control}
                            name="agilityLadder"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Agility Ladder</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                  <FormControl>
                                    <SelectTrigger className="input-focus rounded-2xl">
                                      <SelectValue placeholder="Select agility ladder access" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="yes">Yes</SelectItem>
                                    <SelectItem value="no">No</SelectItem>
                                    <SelectItem value="chalk_tape_ladder">Chalk or tape ladder</SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 4: Equipment & Facilities */}
                  {currentStep === 4 && (
                    <div className="form-section">
                      <h3 className={`text-2xl font-semibold mb-6 ${getStepColor(4)}`}>
                        {getStepTitle(4)}
                      </h3>
                      <OptionalBanner />
                      <div className="space-y-8">
                        {/* Loaded Carries & Sleds */}
                        <div className="space-y-6">
                          <h4 className="text-lg font-medium text-gray-800 dark:text-gray-100">Loaded Carries & Sleds (Select all weight ranges)</h4>
                          
                          {/* Farmer's Carry */}
                          <FormField
                            control={form.control}
                            name="farmersCarry"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Farmer's Carry Handles or Substitutes</FormLabel>
                                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                                  {["1–20 kg", "21–40 kg", "41–60 kg", "61+ kg", "None"].map((weight) => (
                                    <label key={weight} className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={field.value?.includes(weight)}
                                        onCheckedChange={(checked) => {
                                          handleWeightRangeToggle(
                                            "farmersCarry",
                                            ["1–20 kg", "21–40 kg", "41–60 kg", "61+ kg", "None"],
                                            checked === true,
                                            weight,
                                            field.value || [],
                                            field.onChange
                                          );
                                        }}
                                      />
                                      <span className="text-sm text-white">{weight}</span>
                                    </label>
                                  ))}
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Sled or Push Object */}
                          <FormField
                            control={form.control}
                            name="sledAccess"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Sled or Push Object</FormLabel>
                                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                                  {["1–40 kg", "41–80 kg", "81–120 kg", "121+ kg", "None"].map((weight) => (
                                    <label key={weight} className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={field.value?.includes(weight)}
                                        onCheckedChange={(checked) => {
                                          handleWeightRangeToggle(
                                            "sledAccess",
                                            ["1–40 kg", "41–80 kg", "81–120 kg", "121+ kg", "None"],
                                            checked === true,
                                            weight,
                                            field.value || [],
                                            field.onChange
                                          );
                                        }}
                                      />
                                      <span className="text-sm text-white">{weight}</span>
                                    </label>
                                  ))}
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        {/* Mobility & Recovery */}
                        <FormField
                          control={form.control}
                          name="mobilityEquipment"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-lg font-medium text-gray-800 dark:text-gray-100">Mobility & Recovery (Select all that apply)</FormLabel>
                              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mt-4">
                                {["Yoga mat", "Carpeted floor", "Foam roller", "Towel or blanket on floor", "None"].map((equipment) => (
                                  <label key={equipment} className="flex items-center space-x-2">
                                    <Checkbox
                                      checked={field.value?.includes(equipment)}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          field.onChange([...field.value, equipment]);
                                        } else {
                                          field.onChange(field.value?.filter(e => e !== equipment));
                                        }
                                      }}
                                    />
                                    <span className="text-sm text-white">{equipment}</span>
                                  </label>
                                ))}
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Racks */}
                        <FormField
                          control={form.control}
                          name="rackAccess"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-lg font-medium">Racks</FormLabel>
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                                {[
                                  { value: "full_power_rack", label: "Full power rack" },
                                  { value: "half_rack", label: "Half rack" },
                                  { value: "squat_stand", label: "Squat stand" },
                                  { value: "none", label: "None (lift outdoors or improvisation)" }
                                ].map((option) => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => field.onChange(option.value)}
                                    className={`p-4 rounded-2xl border-2 transition-all text-center ${
                                      field.value === option.value
                                        ? "border-gray-400 bg-gray-50 dark:bg-white/10"
                                        : "border-gray-200 dark:border-[var(--dark-3)] hover:border-gray-400"
                                    }`}
                                  >
                                    <div className="font-medium text-sm">{option.label}</div>
                                  </button>
                                ))}
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Hoop or Jumping Surface Access */}
                        <FormField
                          control={form.control}
                          name="courtAccess"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-lg font-medium">Hoop or Jumping Surface Access</FormLabel>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                {[
                                  { value: "indoor_gym", label: "Indoor gym" },
                                  { value: "outdoor_court", label: "Outdoor court" },
                                  { value: "flat_ground_wall", label: "Just flat ground or wall" }
                                ].map((option) => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => field.onChange(option.value)}
                                    className={`p-4 rounded-2xl border-2 transition-all text-center ${
                                      field.value === option.value
                                        ? "border-gray-400 bg-gray-50 dark:bg-white/10"
                                        : "border-gray-200 dark:border-[var(--dark-3)] hover:border-gray-400"
                                    }`}
                                  >
                                    <div className="font-medium">{option.label}</div>
                                  </button>
                                ))}
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 5: Injury History & Optional Stats */}
                  {currentStep === 5 && (
                    <div className="form-section">
                      <h3 className={`text-2xl font-semibold mb-6 ${getStepColor(5)}`}>
                        {getStepTitle(5)}
                      </h3>
                      <OptionalBanner />
                      <div className="space-y-8">
                        {/* Injury History */}
                        <FormField
                          control={form.control}
                          name="injuryHistory"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-lg font-medium text-white">Injury History (Select all that apply)</FormLabel>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                                {[
                                  "Knee pain or injury",
                                  "Ankle sprain or instability", 
                                  "Hip or groin strain",
                                  "Shin splints",
                                  "Lower back pain",
                                  "Hamstring or quad strain",
                                  "Core or abdominal injury",
                                  "Shoulder, elbow, or wrist issue",
                                  "No injuries"
                                ].map((injury) => (
                                  <label key={injury} className="flex items-center space-x-2">
                                    <Checkbox
                                      checked={field.value?.includes(injury)}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          // If "No injuries" is selected, clear other selections
                                          if (injury === "No injuries") {
                                            field.onChange(["No injuries"]);
                                          } else {
                                            // If other injury is selected, remove "No injuries"
                                            const newValue = field.value?.filter(i => i !== "No injuries") || [];
                                            field.onChange([...newValue, injury]);
                                          }
                                        } else {
                                          field.onChange(field.value?.filter(i => i !== injury));
                                        }
                                      }}
                                    />
                                    <span className="text-sm text-white">{injury}</span>
                                  </label>
                                ))}
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Other Injury Specification */}
                        <FormField
                          control={form.control}
                          name="otherInjury"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Other Injury (please specify)</FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="Describe any other injuries..."
                                  className="input-focus rounded-2xl"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Optional Stats */}
                        <div className="space-y-6">
                          <h4 className="text-lg font-medium text-white">Optional Stats (if known)</h4>
                          <p className="text-sm text-white">These are optional; they help us better tailor strength and loading recommendations.</p>
                          {/* PR Type and Unit Toggles */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                              control={form.control}
                              name="prType"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>PR Type</FormLabel>
                                  <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value || undefined}>
                                      <SelectTrigger className="input-focus rounded-2xl">
                                        <SelectValue placeholder="Select PR Type" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="1RM">1-Rep Max (1RM)</SelectItem>
                                        <SelectItem value="3RM">Best Triple (3RM)</SelectItem>
                                        <SelectItem value="5RM">5RM</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="prUnit"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Units</FormLabel>
                                  <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value || undefined}>
                                      <SelectTrigger className="input-focus rounded-2xl">
                                        <SelectValue placeholder="Select Unit" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="kg">kg</SelectItem>
                                        <SelectItem value="lb">lb</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          {/* Lifts */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                              control={form.control}
                              name="standingVertical"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Standing Vertical Jump (cm)</FormLabel>
                                  <FormControl>
                                    <Input 
                                      type="number" 
                                      placeholder="Enter vertical jump height"
                                      className="input-focus rounded-2xl"
                                      {...field}
                                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="runningVertical"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Running Vertical Jump (cm)</FormLabel>
                                  <FormControl>
                                    <Input 
                                      type="number" 
                                      placeholder="Enter running vertical"
                                      className="input-focus rounded-2xl"
                                      {...field}
                                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="armSpan"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Arm Span (cm)</FormLabel>
                                  <FormControl>
                                    <Input 
                                      type="number" 
                                      placeholder="Enter arm span"
                                      className="input-focus rounded-2xl"
                                      {...field}
                                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            {/* Dynamic unit label for strength PRs */}
                            {(() => { const unit = form.watch('prUnit') || 'kg'; return (
                              <>
                                <FormField
                                  control={form.control}
                                  name="squatMax"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Squat PR ({form.watch('prType') || '1RM'}) [{unit}]</FormLabel>
                                      <FormControl>
                                        <Input 
                                          type="number" 
                                          placeholder={`Enter squat ${form.watch('prType') || '1RM'}`}
                                          className="input-focus rounded-2xl"
                                          {...field}
                                          onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={form.control}
                                  name="deadliftMax"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Deadlift PR ({form.watch('prType') || '1RM'}) [{unit}]</FormLabel>
                                      <FormControl>
                                        <Input 
                                          type="number" 
                                          placeholder={`Enter deadlift ${form.watch('prType') || '1RM'}`}
                                          className="input-focus rounded-2xl"
                                          {...field}
                                          onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={form.control}
                                  name="bulgarianSplitSquatPR"
                                  render={({ field }) => (
                                    <FormItem className="md:col-span-2">
                                      <FormLabel>Bulgarian Split Squat PR (single leg) [{unit}]</FormLabel>
                                      <FormControl>
                                        <Input 
                                          type="number" 
                                          placeholder="Enter BSS single-leg PR weight"
                                          className="input-focus rounded-2xl"
                                          {...field}
                                          onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </>
                            )})()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex justify-between pt-8">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={prevStep}
                      disabled={currentStep === 1}
                      className="px-8 py-3 rounded-2xl"
                    >
                      Previous
                    </Button>
                    
                    {currentStep < totalSteps ? (
                      <Button
                        type="button"
                        onClick={async (e) => {
                          e.preventDefault();
                          logger.debug("Next button clicked on step:", currentStep);
                          await nextStep();
                        }}
                        className="btn-primary px-8 py-3 rounded-2xl"
                      >
                        Next
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        className="btn-primary px-12 py-4 rounded-2xl text-white font-semibold text-lg shadow-lg"
                        disabled={generatePlanMutation.isPending}
                        onClick={async (e) => {
                          logger.info("Generate Plan button clicked");
                          const isValid = await form.trigger(); // Trigger validation
                          logger.debug("Form valid:", isValid);
                          logger.debug("Form errors:", form.formState.errors);
                          if (!isValid) {
                            e.preventDefault();
                            toast({
                              title: "Form Incomplete",
                              description: "Please fill out all required fields before generating your plan.",
                              variant: "destructive",
                            });
                          }
                        }}
                      >
                        {generatePlanMutation.isPending ? "Generating Plan (Please wait 5+ minutes)..." : "Generate My Plan"}
                      </Button>
                    )}
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
